import { action, makeObservable, observable } from 'mobx';
import { LumiDB, SourceFileMetadata, TableMetadata, ThreeJsResponse } from '@lumidb/lumidb';
import { LumiDBClient } from '@shared/reality-capture/LumiDBClient';
import { FileTreeService, IFileTreeFlattened } from '@shared/services/FileTreeService';
import { RealityCapturePoint } from '@typings/reality-capture/common';
import { LumiDbCrossSectionInput } from '@typings/reality-capture/lumidb/types';
import { PointCloudPoint } from '@pages/cross-section/cross-section/CrossSectionStore';
import { Vector3 } from '@math.gl/core';
import { ICrossSectionModel } from '@pages/cross-section/shared/CrossSectionWindow';
import { RcPointsToCrossSectionModel } from './utils/dem-utils';
import { pointCloudFilter, extractTokenExpiration, FileNode } from './utils/lumidb-utils';
import { getRandomPastelColor, RcPointsToCrossSectionPoints } from './utils/common';

class LumiDbStore {
    available: boolean;
    token: string | null = null;
    lumidb: LumiDB | null = null;
    treeData: ITreeData = null;

    tableMetadata: TableMetadata | null = null;
    sourceFileFilter: number[];

    selectedFiles: SourceFileMetadata[] = [];

    private tokenExpiration: Date = new Date(0);

    private tokenRequestPromise: Promise<string> | null = null;
    private loadingPromise: Promise<SourceFileMetadata[]> | null = null;

    constructor() {
        this.available = kuura.hasPrivilege('AGGREGATED_POINTCLOUDS');
        makeObservable(this, {
            lumidb: observable,
            token: observable,
            tableMetadata: observable,
            selectedFiles: observable,
            initLumiDb: action,
            setSelectedFiles: action,
        });
    }

    async initLumiDb(treeData: ITreeData) {
        console.log('Initializing LumiDB...');
        if (!this.available) {
            return;
        }

        this.treeData = treeData;
        this.lumidb = new LumiDB({ baseUrl: 'https://api.lumidb.com' });

        this.lumidb.setAuthentication({
            getTokenCallback: async () => {
                // If token is valid, return it
                if (this.token && this.tokenExpiration > new Date()) {
                    return this.token;
                }

                // If a token request is already in progress, wait for it
                if (this.tokenRequestPromise) {
                    return this.tokenRequestPromise;
                }

                // Otherwise, create a new token request
                this.tokenRequestPromise = (async () => {
                    const projectUuid: string = kuura.getProjectUuid();
                    const token: string = await LumiDBClient.getTokenForTable(projectUuid);
                    const decodedToken = Buffer.from(token, 'base64').toString();
                    const exp: number = extractTokenExpiration(decodedToken);
                    this.tokenExpiration = new Date(exp * 1000);
                    this.token = token;
                    return token;
                })();

                try {
                    return await this.tokenRequestPromise;
                } finally {
                    this.tokenRequestPromise = null;
                }
            },
        });

        this.loadingPromise = this.initProjectTableMetadata();

        this.loadingPromise.finally(() => {
            this.loadingPromise = null;
        });
    }

    public async getPointCloudFileUuids(): Promise<string[]> {
        if (this.loadingPromise) {
            await this.loadingPromise;
        }

        return this.selectedFiles.map(file => file.user_data.file_uuid as string);
    }

    public getSelectedFileIds(): number[] {
        if (!this.tableMetadata?.file_metadata) return [];

        const ids: number[] = [];
        const selectedAssetIdSet: Set<string> = new Set(this.selectedFiles.map(file => file.asset_id));
        for (const [key, file] of Object.entries(this.tableMetadata.file_metadata)) {
            if (selectedAssetIdSet.has(file.asset_id)) {
                ids.push(parseInt(key));
            }
        }
        return ids;
    }

    private async initProjectTableMetadata(): Promise<SourceFileMetadata[]> {
        if (!this.lumidb) {
            console.error('LumiDB is not initialized');
            return;
        }

        // Initialize project table metadata here
        const originalMetadata: TableMetadata | undefined = await this.lumidb.getTableMetadata(kuura.getProjectUuid());
        if (!originalMetadata?.file_metadata) {
            // Project has no LumiDB table / metadata yet — nothing to select.
            this.tableMetadata = originalMetadata ?? null;
            this.selectedFiles = [];
            return this.selectedFiles;
        }

        const allNodes: IFileTreeFlattened = FileTreeService.flattenFiletree(this.treeData, true, false, true, () => true);
        const pointCloudsFiles: FileNode[] = allNodes.nodes.filter(pointCloudFilter);
        const pointCloudUuidSet: Set<string> = new Set(pointCloudsFiles.map(file => file.uuid));

        // Filter file_metadata to only include files where user_data.file_uuid is in pointCloudUuidSet
        const filteredFileMetadata: Record<number, SourceFileMetadata> = {};

        for (const [key, file] of Object.entries(originalMetadata.file_metadata)) {
            if (pointCloudUuidSet.has(file.user_data.file_uuid as string)) {
                filteredFileMetadata[parseInt(key)] = file;
            }
        }

        // Create the filtered metadata object
        const metadataWithActivePointClouds: TableMetadata = {
            ...originalMetadata,
            file_metadata: filteredFileMetadata,
        };

        this.tableMetadata = metadataWithActivePointClouds;
        this.selectedFiles = this.tableMetadata.file_metadata ? Object.values<SourceFileMetadata>(this.tableMetadata.file_metadata) : [];

        return this.selectedFiles;
    }

    getSourceFileFilterFromUuids(uuids: string[]): number[] {
        if (!this.tableMetadata || !this.tableMetadata.file_metadata) return [];

        const uuidSet: Set<string> = new Set(uuids);
        const fileIds: number[] = [];

        for (const [key, file] of Object.entries(this.tableMetadata.file_metadata)) {
            if (uuidSet.has(file.user_data.file_uuid as string)) {
                fileIds.push(parseInt(key));
            }
        }

        return fileIds;
    }

    /** File uuids of every point cloud this project has in LumiDB, regardless of cross-section coverage. */
    getAvailableUuids(): string[] {
        if (!this.tableMetadata?.file_metadata) return [];
        return Object.values<SourceFileMetadata>(this.tableMetadata.file_metadata)
            .map(file => file.user_data.file_uuid as string)
            .filter(Boolean);
    }

    getFileNameFromId(fileId: number): string {
        if (!this.tableMetadata || !this.tableMetadata.file_metadata) return 'UNRECOGNIZED POINT CLOUD';

        const file: SourceFileMetadata | undefined = this.tableMetadata.file_metadata[fileId];
        return file ? file.original_filename : 'UNRECOGNIZED POINT CLOUD';
    }

    getUuidFromId(fileId: number): string | null {
        if (!this.tableMetadata || !this.tableMetadata.file_metadata) return null;
        const file: SourceFileMetadata | undefined = this.tableMetadata.file_metadata[fileId];
        return file ? (file.user_data.file_uuid as string) : null;
    }

    setSelectedFiles(fileIds: number[]) {
        if (!this.tableMetadata?.file_metadata) {
            this.selectedFiles = [];
            return;
        }

        const fileIdSet: Set<number> = new Set(fileIds);

        const newSelectedFiles: SourceFileMetadata[] = [];

        for (const [key, file] of Object.entries(this.tableMetadata.file_metadata)) {
            if (fileIdSet.has(parseInt(key))) {
                newSelectedFiles.push(file);
            }
        }
        console.log('Setting selected files:', newSelectedFiles);
        this.selectedFiles = newSelectedFiles;
    }

    async getPointsInsideRegion(region: number[][], sourceFileFilter: number[] | null = null): Promise<RealityCapturePoint[]> {
        if (!Array.isArray(region) || region.length < 3) {
            throw new Error('Region must be an array of at least 3 [x,y] points');
        }

        const normalized = [...region];
        const [fx, fy] = normalized[0];
        const [lx, ly] = normalized[normalized.length - 1];
        if (fx !== lx || fy !== ly) normalized.push([fx, fy]);

        const tableName = kuura.getProjectUuid();

        // Explicitly construct the polygon tuple structure
        const polygon: [number[][][], [number, number]] = [[normalized], [-10000, 10000]];

        console.log('Source file filter:', sourceFileFilter);
        const body = {
            tableName,
            queryBoundary: {
                Polygon: polygon,
            },
            queryProj: 'EPSG:3857',
            maxPoints: 1000,
            maxDensity: 1000.0,
            sourceFileFilter: sourceFileFilter,
            classFilter: null,
            outputType: 'threejs' as const,
        };

        // Metadata is initialized by the caller (lumiPointsToCrossData) before the filter is built.
        // No LumiDB table / point-cloud metadata for this project — the spatial query
        // would 404 against a non-existent table, so skip it and return no points.
        if (!this.tableMetadata?.file_metadata) return [];

        const resp: ThreeJsResponse = await this.lumidb.query(body);

        const pointArray: RealityCapturePoint[] = this.threeJsResponseToPointArray(resp);
        return pointArray;
    }

    private threeJsResponseToPointArray(threejsResponse: ThreeJsResponse): RealityCapturePoint[] {
        const offset = new Vector3(threejsResponse.offset);
        const positionArray = threejsResponse.pointGeometry.attributes.position.array;
        const points: Vector3[] = [];

        // Position array contains x, y, z values sequentially
        for (let i = 0; i < positionArray.length; i += 3) {
            const x = positionArray[i];
            const y = positionArray[i + 1];
            const z = positionArray[i + 2];

            // Create position vector and add offset to get real world coordinates
            const position = new Vector3(x, y, z).add(offset);
            points.push(position);
        }

        const colors: [number, number, number][] = [];
        const colorArray = threejsResponse.pointGeometry.attributes.color.array;

        for (let i = 0; i < colorArray.length; i += 3) {
            const r = colorArray[i];
            const g = colorArray[i + 1];
            const b = colorArray[i + 2];
            colors.push([r, g, b]);
        }

        const sourceFileIds: number[] = threejsResponse.pointGeometry.attributes.source_file_id.array as number[];

        const realityCapturePoints: RealityCapturePoint[] = [];

        for (let i = 0; i < points.length; i++) {
            realityCapturePoints.push({
                point: points[i],
                color: colors[i],
                sourceFileId: sourceFileIds[i],
            });
        }

        return realityCapturePoints;
    }

    async lumiPointsToCrossData(
        input: LumiDbCrossSectionInput
    ): Promise<{ lumiDbModels: ICrossSectionModel[]; lumiDbPoints: PointCloudPoint[]; lumiDbUuids: string[]; availableLumiDbUuids: string[] }> {
        /**
         * Fetches LumiDB points and converts to cross-section models and points.
         */

        const lumiDbUuids: string[] = [];
        const lumiDbModels: ICrossSectionModel[] = [];
        const lumiDbPoints: PointCloudPoint[] = [];

        try {
            // Metadata must be initialized BEFORE resolving the source-file filter — getSourceFileFilterFromUuids
            // reads this.tableMetadata, which is otherwise still null on the first cross-section of a session.
            // A stale/empty filter let LumiDB-served files slip through and get re-fetched as DEMs.
            await this.initProjectTableMetadata();
            const sourceFileFilter = this.getSourceFileFilterFromUuids(input.LumiDbUuids);
            const points = await this.getPointsInsideRegion(input.area, sourceFileFilter);

            points.forEach(p => {
                p.sourceFileUuid = this.getUuidFromId(p.sourceFileId);
                p.name = this.getFileNameFromId(p.sourceFileId);
            });

            const newLumiDbPoints = await RcPointsToCrossSectionPoints({
                points,
                startVector: input.startVector,
                endVector: input.endVector,
                mercatorDistortion: input.mercatorDistortion,
                csCanvas: input.csCanvas,
                pointCloudNamesMap: input.pointCloudNamesMap,
            });
            lumiDbPoints.push(...newLumiDbPoints);

            const groupedPoints: Record<number, PointCloudPoint[]> = {};
            newLumiDbPoints.forEach(p => {
                if (p.sourceFileId) {
                    groupedPoints[p.sourceFileId] ??= [];
                    groupedPoints[p.sourceFileId].push(p);
                    input.pointCloudNamesMap[p.sourceFileId] = p.name;
                }
            });

            Object.entries(groupedPoints).forEach(([sourceFileId], index) => {
                const id = parseInt(sourceFileId, 10);
                const color = index === 0 ? '0,0,0' : getRandomPastelColor();
                const model = RcPointsToCrossSectionModel({
                    points: groupedPoints[id],
                    modelId: id,
                    uuid: this.getUuidFromId(id),
                    color,
                });
                // RcPointsToCrossSectionModel returns null for empty point groups; skip those
                // so realityCaptureModels never contains null entries.
                if (!model) return;
                lumiDbModels.push(model);
                lumiDbUuids.push(this.getUuidFromId(id));
            });
        } catch (error) {
            console.error('Error in lumiPointsToCrossSectionModels:', error);
        }

        // availableLumiDbUuids = every point cloud LumiDB holds for this project, even those with no
        // points in this cross-section. DEM fetching dedups against this so a LumiDB-backed file is
        // never re-fetched as a DEM.
        return { lumiDbModels, lumiDbPoints, lumiDbUuids, availableLumiDbUuids: this.getAvailableUuids() };
    }
}

export const lumiDbStore = new LumiDbStore();
